import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Plus, ChevronDown, ChevronRight, ArrowRight, Zap, Globe } from 'lucide-react'
import { tokenManager } from '../../lib/api'
import { useViewportContext } from '../../hooks/useViewportContext'
import ScorecardItemCard, { ScorecardItemData } from '../../components/scorecard/ScorecardItemCard'
import SpecSearchDropdown from '../../components/scorecard/SpecSearchDropdown'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

// ── Types ──────────────────────────────────────────────────────────

interface ScorecardSection {
  id: number
  title: string
  description: string
  importance: 'nice_to_have' | 'important' | 'mission_critical'
  order: number
  items: ScorecardItemData[]
  isExpanded?: boolean
}

interface Scorecard {
  id: number
  name: string
  description: string
  scorecard_type: string
  scorecard_type_display: string
  is_template: boolean
  is_draft?: boolean
  sections: ScorecardSection[]
  program_id?: number
  solution_family?: {
    id: string
    name: string
  } | null
}

type TabKey =
  | 'foundational'
  | 'technical'
  | 'manufacturing_readiness'
  | 'vc_metrics'
  | 'dod_partnership'

// All program-level scorecards (solution_family == null). Family technical
// scorecards live on the solutions page, not here.
const TAB_CONFIG: { key: TabKey; label: string }[] = [
  { key: 'foundational', label: 'Basic Requirements' },
  { key: 'technical', label: 'Technical Performance' },
  { key: 'manufacturing_readiness', label: 'Production Readiness' },
  { key: 'vc_metrics', label: 'Company Stability' },
  { key: 'dod_partnership', label: 'DoW Partnership' },
]

// Scorecards the user builds by hand (vs. template-copied). These get the
// "Add Section" affordance.
const USER_BUILT_TYPES: TabKey[] = ['foundational', 'technical']

// ── Component ──────────────────────────────────────────────────────

interface ScorecardFullViewProps {
  /** When embedded in a module page: drop the page chrome, take ids from props,
   *  and (with focusType) lock to a single scorecard tab. */
  embedded?: boolean
  programId?: string
  focusType?: TabKey
}

const ScorecardFullView: React.FC<ScorecardFullViewProps> = ({
  embedded = false, programId: programIdProp, focusType,
} = {}) => {
  const params = useParams<{ programId: string }>()
  const programId = programIdProp ?? params.programId
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabKey>(focusType ?? 'foundational')
  const [scorecards, setScorecards] = useState<Scorecard[]>([])
  const [loading, setLoading] = useState(true)
  const [addingToSection, setAddingToSection] = useState<number | null>(null)
  const [addingSection, setAddingSection] = useState(false)
  const [newSectionTitle, setNewSectionTitle] = useState('')
  const [sectionError, setSectionError] = useState<string | null>(null)
  const [rescoring, setRescoring] = useState(false)
  const [rescoringMode, setRescoringMode] = useState<'fast' | 'deep' | null>(null)

  // Tier 2: Register viewport serializer for agent awareness
  useViewportContext('scorecard_edit', () => ({
    program_id: programId,
    active_tab: activeTab,
    scorecard_count: scorecards.length,
    active_scorecard: activeScorecard ? {
      id: activeScorecard.id,
      name: activeScorecard.name,
      type: activeScorecard.scorecard_type,
      is_draft: activeScorecard.is_draft,
      sections: activeScorecard.sections.map(s => ({
        id: s.id,
        title: s.title,
        item_count: s.items.length,
        items: s.items.map(i => ({
          id: i.id,
          title: i.title,
          importance: i.importance,
          spec_name: i.spec_name,
        })),
      })),
    } : null,
  }), [programId, activeTab, scorecards])

  // ── Data loading ───────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!programId) return
    setLoading(true)
    const token = tokenManager.getAccessToken()

    try {
      // Fetch scorecards
      const scRes = await fetch(
        `${API_BASE_URL}/api/scorecards/?program=${programId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      if (scRes.ok) {
        const scData = await scRes.json()
        const cards = (scData.results || scData || []).map((sc: any) => ({
          ...sc,
          sections: (sc.sections || []).map((s: any) => ({
            ...s,
            isExpanded: true,
          })),
        }))
        setScorecards(cards)
      }
    } catch (err) {
      console.error('Failed to load scorecard data:', err)
    } finally {
      setLoading(false)
    }
  }, [programId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Re-fetch when the agent mutates scorecard or program data
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.resource === 'scorecard' || detail?.resource === 'program') {
        console.log('🔄 Agent changed data, refetching scorecards...', detail)
        fetchData()
      }
    }
    window.addEventListener('agentDataChanged', handler)
    return () => window.removeEventListener('agentDataChanged', handler)
  }, [fetchData])

  // ── Scorecard item operations ──────────────────────────────────

  // Program-level scorecard for the active tab (solution_family == null).
  const activeScorecard = scorecards.find(
    (sc) => sc.scorecard_type === activeTab && !sc.solution_family
  )

  // Debounced API save — batches rapid changes (e.g. threshold dragging)
  const pendingUpdates = useRef<Record<number, { updates: Partial<ScorecardItemData>; timer: ReturnType<typeof setTimeout> }>>({})

  const flushUpdate = useCallback(async (itemId: number, updates: Partial<ScorecardItemData>) => {
    try {
      const token = tokenManager.getAccessToken()
      await fetch(`${API_BASE_URL}/api/scorecard-items/${itemId}/`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      })
    } catch (err) {
      console.error('Failed to update item:', err)
    }
  }, [])

  const updateItem = useCallback((itemId: number, updates: Partial<ScorecardItemData>) => {
    // Immediately update local state for responsiveness
    setScorecards((prev) =>
      prev.map((sc) => ({
        ...sc,
        sections: sc.sections.map((section) => ({
          ...section,
          items: section.items.map((item) =>
            item.id === itemId ? { ...item, ...updates } : item
          ),
        })),
      }))
    )

    // Debounce API call — merge with any pending updates for this item
    const pending = pendingUpdates.current[itemId]
    if (pending) {
      clearTimeout(pending.timer)
      pending.updates = { ...pending.updates, ...updates }
    } else {
      pendingUpdates.current[itemId] = { updates: { ...updates }, timer: setTimeout(() => {}, 0) }
      clearTimeout(pendingUpdates.current[itemId].timer)
    }

    pendingUpdates.current[itemId].timer = setTimeout(() => {
      const merged = pendingUpdates.current[itemId]?.updates
      delete pendingUpdates.current[itemId]
      if (merged) flushUpdate(itemId, merged)
    }, 400)
  }, [flushUpdate])

  const deleteItem = useCallback(async (itemId: number) => {
    try {
      const token = tokenManager.getAccessToken()
      await fetch(`${API_BASE_URL}/api/scorecard-items/${itemId}/`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })

      setScorecards((prev) =>
        prev.map((sc) => ({
          ...sc,
          sections: sc.sections.map((section) => ({
            ...section,
            items: section.items.filter((item) => item.id !== itemId),
          })),
        }))
      )
    } catch (err) {
      console.error('Failed to delete item:', err)
    }
  }, [])

  const addItemFromSpec = useCallback(async (sectionId: number, spec: any) => {
    try {
      const token = tokenManager.getAccessToken()
      const section = activeScorecard?.sections.find((s) => s.id === sectionId)
      const maxOrder = Math.max(0, ...(section?.items.map((i) => i.order) || [0]))

      const response = await fetch(`${API_BASE_URL}/api/scorecard-items/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          section: sectionId,
          title: spec.name,
          description: spec.description || '',
          importance: 'important',
          order: maxOrder + 1,
          spec: spec.id,
          lower_threshold: spec.typical_min ? Number(spec.typical_min) : null,
          upper_threshold: spec.typical_max ? Number(spec.typical_max) : null,
        }),
      })

      if (response.ok) {
        const newItem = await response.json()
        setScorecards((prev) =>
          prev.map((sc) => ({
            ...sc,
            sections: sc.sections.map((section) =>
              section.id === sectionId
                ? { ...section, items: [...section.items, { ...newItem, spec_name: spec.name, spec_unit: spec.unit }] }
                : section
            ),
          }))
        )
      }
    } catch (err) {
      console.error('Failed to add item:', err)
    }
    setAddingToSection(null)
  }, [activeScorecard])

  const addSection = useCallback(async () => {
    const title = newSectionTitle.trim()
    if (!activeScorecard || !title) return
    setSectionError(null)
    try {
      const token = tokenManager.getAccessToken()
      const maxOrder = Math.max(0, ...(activeScorecard.sections.map((s) => s.order) || [0]))
      const response = await fetch(`${API_BASE_URL}/api/scorecard-sections/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          scorecard: activeScorecard.id,
          title,
          order: maxOrder + 1,
        }),
      })
      if (response.ok) {
        const newSection = await response.json()
        setScorecards((prev) =>
          prev.map((sc) =>
            sc.id === activeScorecard.id
              ? { ...sc, sections: [...sc.sections, { ...newSection, items: newSection.items || [], isExpanded: true }] }
              : sc
          )
        )
        setNewSectionTitle('')
        setAddingSection(false)
      } else {
        const data = await response.json().catch(() => ({}))
        // unique_together (scorecard, title) → friendly message
        const msg =
          (Array.isArray(data.title) && data.title[0]) ||
          data.detail ||
          (Array.isArray(data.non_field_errors) && data.non_field_errors[0]) ||
          'Could not add section. A section with that title may already exist.'
        setSectionError(msg)
      }
    } catch (err) {
      console.error('Failed to add section:', err)
      setSectionError('Could not add section.')
    }
  }, [activeScorecard, newSectionTitle])

  const toggleSection = (sectionId: number) => {
    setScorecards((prev) =>
      prev.map((sc) => ({
        ...sc,
        sections: sc.sections.map((section) =>
          section.id === sectionId
            ? { ...section, isExpanded: !section.isExpanded }
            : section
        ),
      }))
    )
  }

  const handleRescore = useCallback(async (mode: 'fast' | 'deep') => {
    if (!activeScorecard) return

    setRescoring(true)
    setRescoringMode(mode)

    try {
      const token = tokenManager.getAccessToken()
      const response = await fetch(
        `${API_BASE_URL}/api/scorecards/${activeScorecard.id}/rescore/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ mode }),
        }
      )

      if (response.ok) {
        const result = await response.json()
        const skipped = result.skipped_products?.length || 0
        if (mode === 'fast') {
          alert(
            `Fast rescore complete!\n` +
            `Products rescored: ${result.products_rescored}\n` +
            `Observations created: ${result.total_observations_created}\n` +
            `Failed: ${result.total_observations_failed}` +
            (skipped ? `\nSkipped (no description, need Deep): ${skipped}` : '')
          )
        } else {
          alert(
            `Deep rescore queued!\n` +
            `Queued ${result.products_queued} of ${result.candidate_count ?? result.products_queued} candidates for Perplexity research.\n` +
            (skipped ? `Skipped ${skipped} (${result.skipped_reason || 'cap'}).\n` : '') +
            `This will take a few minutes. Check back soon.`
          )
        }
      } else {
        const error = await response.json()
        alert(`Rescore failed: ${error.error || 'Unknown error'}`)
      }
    } catch (err) {
      console.error('Rescore error:', err)
      alert(`Rescore failed: ${err}`)
    } finally {
      setRescoring(false)
      setRescoringMode(null)
    }
  }, [activeScorecard])

  // ── Render ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  const isUserBuiltTabActive = USER_BUILT_TYPES.includes(activeTab)

  return (
    <div className={embedded ? 'flex flex-col' : 'h-full flex flex-col overflow-hidden'}>
      {/* Scrollable content */}
      <div className={embedded ? '' : 'flex-1 overflow-y-auto'}>
        <div className={embedded ? 'space-y-8' : 'max-w-[800px] mx-auto px-6 py-8 space-y-10'}>

          {/* Page Header (title hidden when embedded in a module page) */}
          <div className="flex items-start justify-between">
            {!embedded && <h1 className="text-2xl font-bold text-foreground">Requirements</h1>}
            {embedded && <div />}

            {/* Rescore Buttons — hidden in the embedded module page; these belong
                on Market View, not on the Basic Requirements step (DEV-732). */}
            {activeScorecard && !embedded && (
              <div className="flex gap-2">
                <button
                  onClick={() => handleRescore('fast')}
                  disabled={rescoring}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium
                             text-primary bg-primary/10 hover:bg-primary/20
                             disabled:opacity-50 disabled:cursor-not-allowed
                             rounded-lg transition-colors"
                >
                  <Zap className="w-4 h-4" />
                  {rescoring && rescoringMode === 'fast' ? 'Rescoring...' : 'Fast Rescore'}
                </button>
                <button
                  onClick={() => handleRescore('deep')}
                  disabled={rescoring}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium
                             text-primary bg-primary/10 hover:bg-primary/20
                             disabled:opacity-50 disabled:cursor-not-allowed
                             rounded-lg transition-colors"
                >
                  <Globe className="w-4 h-4" />
                  {rescoring && rescoringMode === 'deep' ? 'Queueing...' : 'Deep Rescore'}
                </button>
              </div>
            )}
          </div>

          {/* Requirement Specifications Section */}
          <div>
            {/* Primary Category Tabs — all program-level scorecards (null family).
                Hidden when focused to a single scorecard inside a module page. */}
            <div className={`flex gap-1 mb-5 border-b border-border ${focusType ? 'hidden' : ''}`}>
              {TAB_CONFIG.map((tab) => {
                const isActive = activeTab === tab.key
                const sc = scorecards.find((s) => s.scorecard_type === tab.key && !s.solution_family)
                const itemCount = sc?.sections.reduce((sum, s) => sum + s.items.length, 0) || 0
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'border-b-2 border-primary text-primary -mb-[1px]'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {tab.label}
                    {itemCount > 0 && (
                      <span className={`ml-1.5 text-xs ${isActive ? 'text-primary/60' : 'text-muted-foreground/60'}`}>({itemCount})</span>
                    )}
                  </button>
                )
              })}
            </div>

            {/* Active Tab Content */}
            {activeScorecard ? (
              <div className="space-y-6">
            {activeScorecard.sections.map((section) => (
              <div key={section.id}>
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center gap-2 mb-2 group/section"
                >
                  {section.isExpanded ? (
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                  )}
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground group-hover/section:text-foreground transition-colors">
                    {section.title}
                  </span>
                  <span className="text-[10px] text-muted-foreground/60">{section.items.length}</span>
                  <div className="flex-1 h-px bg-border/50 ml-2" />
                </button>

                {/* Section Content */}
                {section.isExpanded && (
                  <div className="ml-5 space-y-1.5">
                    {section.items.map((item) => (
                      <ScorecardItemCard
                        key={item.id}
                        item={item}
                        mode="edit"
                        onUpdate={updateItem}
                        onDelete={deleteItem}
                      />
                    ))}

                    {/* Add Item */}
                    {addingToSection === section.id ? (
                      <div className="mt-2">
                        <SpecSearchDropdown
                          onSelectSpec={(spec) => addItemFromSpec(section.id, spec)}
                          onCreateNew={(name) => {
                            addItemFromSpec(section.id, {
                              id: null,
                              name,
                              description: '',
                              unit: '',
                              typical_min: null,
                              typical_max: null,
                            })
                          }}
                          category={activeTab === 'technical' ? 'technical' : undefined}
                          programId={programId}
                          placeholder="Search specs to add as requirement..."
                        />
                        <button
                          onClick={() => setAddingToSection(null)}
                          className="mt-1 text-xs text-muted-foreground hover:text-foreground"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setAddingToSection(section.id)}
                        className="flex items-center gap-1.5 px-4 py-2 mt-1 text-xs text-muted-foreground
                                   hover:text-primary hover:bg-primary/5 rounded-lg transition-colors w-full"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Add Item
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Add Section (Basic Requirements + Technical Performance are user-built) */}
            {isUserBuiltTabActive && (
              addingSection ? (
                <div className="pt-2 space-y-1">
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus
                      value={newSectionTitle}
                      onChange={(e) => { setNewSectionTitle(e.target.value); setSectionError(null) }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') addSection()
                        if (e.key === 'Escape') { setAddingSection(false); setNewSectionTitle(''); setSectionError(null) }
                      }}
                      placeholder="Section title (e.g. Performance)"
                      className="flex-1 px-3 py-1.5 text-sm rounded-md border border-border bg-background"
                    />
                    <button
                      onClick={addSection}
                      disabled={!newSectionTitle.trim()}
                      className="px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => { setAddingSection(false); setNewSectionTitle(''); setSectionError(null) }}
                      className="px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Cancel
                    </button>
                  </div>
                  {sectionError && <p className="text-xs text-destructive">{sectionError}</p>}
                </div>
              ) : (
                <button
                  onClick={() => { setAddingSection(true); setSectionError(null) }}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-muted-foreground
                             hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Section
                </button>
              )
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-48">
            <div className="text-center">
              <p className="text-muted-foreground mb-2">
                No scorecard found for this tab.
              </p>
              <p className="text-sm text-muted-foreground">
                Scorecards are created when the program is set up.
              </p>
            </div>
          </div>
        )}
          </div>

        </div>
      </div>

      {/* Floating "See Matching Products" button — bottom-right, above agent bubble */}
      <button
        onClick={() => navigate(`/programs/${programId}/market`)}
        className="fixed bottom-24 right-6 flex items-center gap-2 px-5 py-2.5 text-sm font-medium
                   text-primary-foreground bg-primary hover:bg-primary/90 rounded-full shadow-lg
                   transition-colors z-30"
      >
        See Matching Products
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  )
}

export default ScorecardFullView
